
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload, Shield } from "lucide-react";

// Declare Zoho functions globally to fix TypeScript errors
declare global {
  interface Window {
    zsValidateMandatoryFields: () => boolean;
    zsResetWebForm: (formId: string) => void;
    zsOpenFileBrowseAttachment: (e: Event | React.SyntheticEvent) => void;
    zsRenderBrowseFileAttachment: (value: string, target: EventTarget) => void;
  }
}

const ContactForm = () => {
  const [_files, setFiles] = useState<File[]>([]);
  const [captchaUrl, setCaptchaUrl] = useState("");
  const [captchaDigest, setCaptchaDigest] = useState("");
  const [consent, setConsent] = useState(false);

  // Fetch CAPTCHA
  const loadCaptcha = () => {
    fetch(
      `https://desk.zoho.com/support/GenerateCaptcha?action=getNewCaptcha&_=${Date.now()}`
    )
      .then((res) => res.json())
      .then((data) => {
        setCaptchaUrl(data.captchaUrl);
        setCaptchaDigest(data.captchaDigest);
        // also set hidden input value
        const hidden: HTMLInputElement = document.querySelector(
          "input[name='xJdfEaS']"
        );
        if (hidden) hidden.value = data.captchaDigest;
      })
      .catch(console.error);
  };

  useEffect(() => {
    // Load Zoho scripts
    const script1 = document.createElement("script");
    script1.src =
      "https://d17nz991552y2g.cloudfront.net/app/js/jqueryandencoder.ef05974972bf3bca1b87.js";
    script1.async = true;
    document.head.appendChild(script1);

    // Add Zoho form validation functionality
    const script2 = document.createElement("script");
    script2.innerHTML = `
      function zsValidateMandatoryFields() {
        var mandatoryFields = document.querySelectorAll('.manfieldbdr');
        var isValid = true;

        mandatoryFields.forEach(function(field) {
          if (field.value.trim() === '') {
            field.style.borderColor = '#ff0000';
            isValid = false;
          } else {
            field.style.borderColor = '#e2e8f0';
          }
        });

        return isValid;
      }

      function zsResetWebForm(formId) {
        document.getElementById('zsWebToCase_' + formId).reset();
      }

      // File upload handling
      document.addEventListener('change', function(e) {
        if (e.target.type === 'file') {
          var fileInput = e.target;
          var fileContainer = document.getElementById('zsFileBrowseAttachments');

          if (fileInput.files.length > 0) {
            var fileName = fileInput.files[0].name;
            var fileDiv = document.createElement('div');
            fileDiv.className = 'filenamecls';
            fileDiv.textContent = fileName;
            fileContainer.appendChild(fileDiv);

            // Show next file input
            var nextInput = document.getElementById('zsattachment_' + (parseInt(fileInput.id.slice(-1)) + 1));
            if (nextInput) {
              nextInput.style.display = 'block';
            }
          }
        }
      });
    `;
    document.head.appendChild(script2);

    // Make functions globally available
    window.zsValidateMandatoryFields = window.eval('zsValidateMandatoryFields');
    window.zsResetWebForm = window.eval('zsResetWebForm');

    loadCaptcha();

    return () => {
      if (document.head.contains(script1)) {
        document.head.removeChild(script1);
      }
      if (document.head.contains(script2)) {
        document.head.removeChild(script2);
      }
    };
  }, []);

  const _handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const _removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Contact form submitted - redirecting to knowledgebase...');

    // Get form data
    const formData = new FormData(e.target as HTMLFormElement);

    // Log the form data for debugging
    console.log('Form data:', Object.fromEntries(formData));

    // Submit to knowledgebase
    try {
      const form = e.target as HTMLFormElement;
      form.submit();
      console.log('Form submitted successfully to knowledgebase');
    } catch (error) {
      console.error('Error submitting form:', error);
      // Fallback: redirect manually
      window.location.href = 'https://knowledgebase.gimmefy.ai/support/WebToCase';
    }
  };

  return (
    <Card className="bg-card border shadow-elegant">
      <CardHeader>
        <CardTitle id="contact-form-title" className="text-2xl font-bold text-center">Get in Touch</CardTitle>
      </CardHeader>
      <CardContent>
        <div id="zohoSupportWebToCase" className="w-full">
          <form
            name="zsWebToCase_914615000005088001"
            id="contact-form"
            action="https://knowledgebase.gimmefy.ai/support/WebToCase"
            method="POST"
            encType="multipart/form-data"
            onSubmit={handleSubmit}
          >
            <input type="hidden" name="xnQsjsdp" value="edbsn6e0669abdb47a9a3d512ed163e4cdf33" />
            <input
              type="hidden"
              name="xmIwtLD"
              value="edbsn44d27dba23bbf319aab4b2d33c320ff223f090c3afaf2f7d2ea588f790764c5e"
            />
            <input type="hidden" name="xJdfEaS" value={captchaDigest} />
            <input type="hidden" name="actionType" value="Q2FzZXM=" />
            <input type="hidden" id="property(module)" value="Cases" />
            <input
              type="hidden"
              id="dependent_field_values_Cases"
              value='{"JSON_VALUES":{},"JSON_SELECT_VALUES":{},"JSON_MAP_DEP_LABELS":[]}'
            />
            <input type="hidden" name="returnURL" value="https://gimmefy.ai/" />

            <div className="space-y-6">
              {/* Name Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact-first-name">First Name *</Label>
                  <Input
                    id="contact-first-name"
                    type="text"
                    maxLength={120}
                    name="First Name"
                    className="manfieldbdr bg-background"
                    placeholder="Enter your first name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-last-name">Last Name *</Label>
                  <Input
                    id="contact-last-name"
                    type="text"
                    maxLength={120}
                    name="Contact Name"
                    className="manfieldbdr bg-background"
                    placeholder="Enter your last name"
                  />
                </div>
              </div>

              {/* Contact Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact-email">Email Address *</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    maxLength={120}
                    name="Email"
                    className="manfieldbdr bg-background"
                    placeholder="Enter your email address"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-phone">Phone Number *</Label>
                  <Input
                    id="contact-phone"
                    type="tel"
                    maxLength={120}
                    name="Phone"
                    className="manfieldbdr bg-background"
                    placeholder="Enter your phone number"
                  />
                </div>
              </div>

              {/* Subject */}
              <div className="space-y-2 mr-6">
                <Label htmlFor="contact-subject">Subject *</Label>
                <select
                  id="contact-subject"
                  name="Subject"
                  className="manfieldbdr w-full p-3 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                >
                  <option value="">Select a subject</option>
                  <option value="general">General Inquiry</option>
                  <option value="support">Technical Support</option>
                  <option value="sales">Sales Question</option>
                  <option value="partnership">Partnership Opportunity</option>
                  <option value="feedback">Feedback</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="contact-message">Message *</Label>
                <Textarea
                  id="contact-message"
                  name="Description"
                  maxLength={3000}
                  className="manfieldbdr bg-background min-h-[120px]"
                  placeholder="Tell us more about your inquiry..."
                />
              </div>

              {/* CAPTCHA */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">Security Verification</span>
                </div>
                <div className="flex items-center gap-4">
                  {captchaUrl && (
                    <img id="contact-captcha-image" src={captchaUrl} alt="CAPTCHA" />
                  )}
                  <Button
                    id="contact-captcha-refresh"
                    type="button"
                    variant="outline"
                    onClick={loadCaptcha}
                    className="ml-2"
                  >
                    ↻
                  </Button>
                </div>
                <Input
                  id="contact-captcha-input"
                  type="text"
                  name="zsWebFormCaptchaWord"
                  placeholder="Enter CAPTCHA"
                  className="mt-2 bg-background"
                />
              </div>

              {/* File Upload */}
              <div className="space-y-2">
                <Label htmlFor="contact-files">Attachments (Optional)</Label>
                <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 text-center bg-background">
                  <Upload className="mx-auto h-6 w-6 text-muted-foreground mb-1" />
                  <p className="text-sm text-muted-foreground mb-1">
                    Drag and drop files here, or click to browse
                  </p>
                  <p className="text-xs text-muted-foreground mb-3">
                    Max file size: 20MB. You can attach up to 5 files.
                  </p>
                  {[1, 2, 3, 4, 5].map((num, index) => (
                    <input
                      key={num}
                      className="wtcuploadinput"
                      type="file"
                      name={`attachment_${num}`}
                      id={`contact-file-${num}`}
                      style={{ display: index === 0 ? "block" : "none" }}
                      onClick={(e) => {
                        if (window.zsOpenFileBrowseAttachment) {
                          window.zsOpenFileBrowseAttachment(e);
                        }
                      }}
                      onChange={(e) => {
                        if (window.zsRenderBrowseFileAttachment) {
                          const target = e.target as HTMLInputElement;
                          window.zsRenderBrowseFileAttachment(target.value, target);
                        }
                      }}
                    />
                  ))}
                  <div className="flex justify-center w-full">
                    <Button
                      id="contact-choose-file"
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById("contact-file-1")?.click()}
                      className="w-auto"
                    >
                      Choose File
                    </Button>
                  </div>
                </div>

                <div id="contact-file-attachments" />
              </div>

              {/* Consent */}
              <div className="space-y-2">
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="contact-consent"
                    checked={consent}
                    onCheckedChange={(checked) => setConsent(!!checked)}
                  />
                  <Label htmlFor="contact-consent" className="text-sm leading-5">
                    I agree to be contacted by the team regarding my inquiry. *
                  </Label>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full"
                id="contact-submit-button"
                disabled={!consent}
              >
                Send Message
              </Button>
            </div>
          </form>
        </div>

        <style>{`
          #zohoSupportWebToCase textarea,
          #zohoSupportWebToCase input[type='text'],
          #zohoSupportWebToCase input[type='date'],
          #zohoSupportWebToCase select,
          .wb_common {
              width: 100%;
          }

          #zohoSupportWebToCase td {
              padding: 11px 5px;
          }

          #zohoSupportWebToCase textarea,
          #zohoSupportWebToCase input[type='text'],
          #zohoSupportWebToCase input[type='date'],
          #zohoSupportWebToCase select {
              border: 1px solid #e2e8f0;
              padding: 8px 12px;
              border-radius: 6px;
              font-size: 14px;
              transition: border-color 0.2s;
          }

          #zohoSupportWebToCase select {
              box-sizing: unset
          }

          #zohoSupportWebToCase .wb_selectDate {
              width: auto
          }

          #zohoSupportWebToCase input.wb_cusInput {
              width: 108px
          }

          .wb_FtCon {
              display: flex;
              align-items: center;
              justify-content: flex-end;
              margin-top: 15px;
              padding-left: 10px
          }

          .wb_logoCon {
              display: flex;
              margin-left: 5px
          }

          .wb_logo {
              max-width: 16px;
              max-height: 16px;
          }

          #zohoSupportWebToCase .wb_multi_pick {
              border: 1px solid #ddd;
              padding: 3px 5px;
              border-radius: 3px;
              width: 280px;
              height: 95px;
              overflow-y: auto;
          }

          #zohoSupportWebToCase .wb_multi_pick_label {
              display: block;
          }

          #zohoSupportWebToCase .wb_multi_pick_input,
          .wb_multi_pick_input_all {
              vertical-align: middle;
              margin-right: 5px;
          }

          .zsFormClass {
              background-color: #FFFFFF;
              width: 100%
          }

          .zsFontClass {
               color: #1e3a8a;
      font-family: inherit;
      font-size: 14px;
          }

          .manfieldbdr {
              /* Removed red left border */
          }

          .hleft {
              text-align: left;
          }

          input[type=file]::-webkit-file-upload-button {
              cursor: pointer;
          }

          .wtcsepcode {
              margin: 0px 15px;
              color: #aaa;
              float: left;
          }

          .wtccloudattach {
              float: left;
              color: #00a3fe !important;
              cursor: pointer;
              text-decoration: none !important;
          }

          .wtccloudattach:hover {
              text-decoration: none !important;
          }

          .wtcuploadinput {
              cursor: pointer;
              float: left;
              width: 62px;
              margin-top: -20px;
              opacity: 0;
              clear: both;
          }

          .wtcuploadfile {
              float: left;
              color: #00a3fe;
          }

          .filenamecls {
              margin-right: 15px;
              float: left;
              margin-top: 5px;
          }

          .clboth {
              clear: both;
          }

          #zsFileBrowseAttachments {
              clear: both;
              margin: 5px 0px 10px;
          }

          .zsFontClass {
              vertical-align: top;
          }

          #tooltip-zc {
              font: normal 12px Arial, Helvetica, sans-serif;
              line-height: 18px;
              position: absolute;
              padding: 8px;
              margin: 20px 0 0;
              background: #fff;
              border: 1px solid #528dd1;
              -moz-border-radius: 5px;
              -webkit-border-radius: 5px;
              border-radius: 5px;
              color: #eee;
              -webkit-box-shadow: 5px 5px 20px rgba(0, 0, 0, 0.2);
              -moz-box-shadow: 5px 5px 20px rgba(0, 0, 0, 0.2);
              z-index: 10000;
              color: #777
          }

          .wtcmanfield {
              color: #f00;
              font-size: 16px;
              position: relative;
              top: 2px;
              left: 1px;
          }

          #zsCloudAttachmentIframe {
              width: 100%;
              height: 100%;
              z-index: 99999 !important;
              position: fixed;
              left: 0px;
              top: 0px;
              border-style: none;
              display: none;
              background-color: #fff;
          }

          .wtchelpinfo {
              background-position: -246px -485px;
              width: 15px;
              height: 15px;
              display: inline-block;
              position: relative;
              top: 2px;
              background-image: url(https://d1ydxa2xvtn0b5.cloudfront.net/app/images/zs-mpro.b6c9cf2347c62390fdcb.png);
          }

          .zsMaxSizeMessage {
              font-size: 13px;
          }
          #zohoSupportWebToCase input[type="submit"] {
              background-color: #ef4444;
              color: white;
              padding: 10px 24px;
              border: none;
              border-radius: 6px;
              cursor: pointer;
              font-weight: 500;
              transition: background-color 0.2s;
          }

          #zohoSupportWebToCase input[type="button"] {
              background-color: transparent;
              color: #6b7280;
              padding: 10px 24px;
              border: 1px solid #d1d5db;
              border-radius: 6px;
              cursor: pointer;
              transition: background-color 0.2s;
          }

          /* Ensure button centering in file upload area */
          .border-dashed .flex.justify-center {
              width: 100%;
              display: flex;
              justify-content: center;
              align-items: center;
          }

          .border-dashed button {
              margin: 0 auto;
              display: block;
          }
        `}</style>
      </CardContent>
    </Card>
  );
};

export default ContactForm;
